import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getEntitiesList } from '@/shared/api/entitiesSharedApi'
import { getHods } from '@/shared/api/usersSharedApi'

import {
  createDepartment,
  deactivateDepartment,
  getDepartment,
  listDepartments,
  reactivateDepartment,
  updateDepartment,
} from '../api/departmentApi'

import type {
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
} from '../api/departmentApi'

// ── Query options factories ─────────────────────────────────────────────────

export const entityOptionsQueryOptions = () =>
  queryOptions({
    queryKey: ['entities-options'] as const,
    queryFn: getEntitiesList,
    staleTime: 1000 * 60 * 5,
  })

export const hodOptionsQueryOptions = () =>
  queryOptions({
    queryKey: ['users', 'hod-options'] as const,
    queryFn: getHods,
    staleTime: 1000 * 60 * 2,
  })

export const departmentsQueryOptions = (entityId: string | undefined) =>
  queryOptions({
    queryKey: ['departments', entityId] as const,
    queryFn: () => listDepartments(entityId as string),
    // Only fire when we have an entity selected; component reads enabled flag
    // via the wrapped useQuery.
    enabled: Boolean(entityId),
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  })

export const departmentDetailQueryOptions = (departmentId: string | undefined) =>
  queryOptions({
    queryKey: ['department', departmentId] as const,
    queryFn: () => getDepartment(departmentId as string),
    enabled: Boolean(departmentId),
    staleTime: 1000 * 60,
  })

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useEntityOptions() {
  return useQuery(entityOptionsQueryOptions())
}

export function useHodOptions() {
  return useQuery(hodOptionsQueryOptions())
}

export function useDepartments(entityId: string | undefined) {
  return useQuery(departmentsQueryOptions(entityId))
}

export function useDepartmentDetail(departmentId: string | undefined) {
  return useQuery(departmentDetailQueryOptions(departmentId))
}

// Mutations: invalidate both the list (scoped by entityId) and the single
// department key. Toast handling for failures is the caller's responsibility
// (modals branch on 409 → ConflictDialog vs other errors → toast).

// Broad-prefix invalidation: department changes ripple into site cards
// (department badge), HOD pickers, and user detail (departments_owned).
function invalidateDepartmentCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  departmentId: string,
  entityId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['departments', entityId] })
  queryClient.invalidateQueries({ queryKey: ['departments'] })
  queryClient.invalidateQueries({ queryKey: ['department', departmentId] })
  queryClient.invalidateQueries({ queryKey: ['users'] })
  queryClient.invalidateQueries({ queryKey: ['user'] })
  queryClient.invalidateQueries({ queryKey: ['entity-detail', entityId] })
  queryClient.invalidateQueries({ queryKey: ['entity-sites', entityId] })
  queryClient.invalidateQueries({ queryKey: ['site-detail'] })
}

export function useCreateDepartment(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateDepartmentRequest) => createDepartment(entityId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments', entityId] })
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateDepartment(departmentId: string, entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateDepartmentRequest) => updateDepartment(departmentId, body),
    onSuccess: () => {
      invalidateDepartmentCaches(queryClient, departmentId, entityId)
    },
  })
}

export function useDeactivateDepartment(departmentId: string, entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deactivateDepartment(departmentId),
    onSuccess: () => {
      invalidateDepartmentCaches(queryClient, departmentId, entityId)
    },
  })
}

export function useReactivateDepartment(departmentId: string, entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => reactivateDepartment(departmentId),
    onSuccess: () => {
      invalidateDepartmentCaches(queryClient, departmentId, entityId)
    },
  })
}
